import { createSlice } from "@reduxjs/toolkit";
import {
  EXTRACTION_PROGRESS_END,
  EXTRACTION_PROGRESS_START,
  PHASE_BY_EVENT,
  PHASE_PROGRESS_FLOOR,
} from "constants/outlineGeneration";
import {
  PROPOSAL_OUTLINE_COMPLETED,
  REQUIREMENT_STATUS,
} from "constants/proposals";
import { pick, uniq } from "lodash";

import { buildDocumentLookup, mergeAnswersLastWins } from "./utils";

export const initialState = {
  message: "",
  data: null,
  documentLookup: {},
  error: null,
  isEditing: false,
  originalSections: null,
  ownerId: null,
  ownerName: null,
  ownerType: null,
  progressHistory: [],
  progressError: null,
  // Machine-readable OutlineErrorCode for a failed run, so the UI can branch on the cause
  // (admin-fixable config vs a transient blip) rather than matching on progressError prose.
  progressErrorCode: null,
  selectedRowIds: [],
  selectedIndex: -1,
  clarification: null,
  structureProposal: null,
  clarificationAnswers: [],
  documentIds: null,
  overallProgress: 0,
  currentPhase: null,
  currentMessage: null,
  documentPipeline: [],
  documentTotal: 0,
  requirementsFound: 0,
  requirementsFeed: [],
  generationStartedAt: null,
  generationFailed: false,
  connectionState: "idle",
  detectedType: null,
  detectedConfidence: null,
  detectedLabel: null,
  gate: null,
  requirements: [],
  outlineDocuments: [],
  // Paragraphs of a source document, keyed by nextgen document id, for the Coverage
  // view's full-document listing. Fetched on demand — an outline can cite a 4000-paragraph
  // solicitation, so this is never part of the outline payload.
  documentParagraphs: {},
};

// Several phases carry no server-supplied percentage on the staged pipeline, so the phase itself
// implies a floor. Always raise, never lower — a real percentage from the server outranks it.
const raiseProgressToPhaseFloor = (state, phase) => {
  const floor = PHASE_PROGRESS_FLOOR[phase];
  if (typeof floor === "number") {
    state.overallProgress = Math.max(state.overallProgress, floor);
  }
};

export const outlineSlice = createSlice({
  name: "proposalOutline",
  initialState,
  reducers: {
    reset: () => initialState,
    resetEditing: (state) => {
      return {
        ...state,
        ...pick(initialState, ["selectedRowIds", "selectedIndex"]),
      };
    },
    setOwner: (state, { payload }) => {
      const { ownerType, ownerId, ownerName } = payload || {};
      state.ownerType = ownerType ?? null;
      state.ownerId = ownerId ?? null;
      state.ownerName = ownerName ?? null;
    },
    clearOwner: (state) => {
      state.ownerType = null;
      state.ownerId = null;
      state.ownerName = null;
    },
    initializeEditing: (state) => {
      state.isEditing = true;
      state.data = {
        createdDate: Date.now(),
        processingStatus: PROPOSAL_OUTLINE_COMPLETED,
        outlineData: {
          sections: [],
        },
      };
    },
    setGenerateFailed: (state, { payload }) => {
      const { message, errorCode } = payload || {};
      state.progressError = message;
      state.progressErrorCode = errorCode || null;
      state.generationFailed = true;
      // NOTE: deliberately does NOT null state.data or wipe the pipeline — the wait screen stays
      // mounted so the failure renders in context with a Retry affordance.
    },
    setOutlineData: (state, { payload }) => {
      state.data = payload;

      const sections = payload?.outlineData?.sections || [];
      state.documentLookup = buildDocumentLookup(sections);
    },
    // Status transitions arrive on their own (SSE events, optimistic pre-request flips) with no
    // outline body attached. They must merge: setOutlineData replaces state.data wholesale, which
    // is correct for a fetch of a different owner but destroys id/locked/retryable/errorMessage
    // when all the caller meant was "this run is running now" — and retryFailedStage, unlike
    // handleGateReady, has no refetch to restore them.
    setProcessingStatus: (state, { payload }) => {
      state.data = { ...(state.data || {}), processingStatus: payload };
    },
    setIsEditing: (state, { payload }) => {
      const entering = Boolean(payload);
      state.isEditing = entering;
      if (entering) {
        state.originalSections = state.data?.outlineData?.sections
          ? JSON.parse(JSON.stringify(state.data.outlineData.sections))
          : null;
      } else {
        state.originalSections = null;
      }
    },
    setIsLocked: (state, { payload = {} }) => {
      const data = state.data;

      state.data = {
        ...data,
        ...payload,
      };
    },
    setProgressHistory: (state, { payload = [] }) => {
      state.progressHistory = payload;
    },
    addProgressHistory: (state, { payload }) => {
      state.progressHistory = state.progressHistory.concat(payload);
    },
    resetGenerationProgress: (state) => {
      state.overallProgress = 0;
      state.currentPhase = null;
      state.currentMessage = null;
      state.documentPipeline = [];
      state.documentTotal = 0;
      state.requirementsFound = 0;
      state.requirementsFeed = [];
      state.generationStartedAt = null;
      state.generationFailed = false;
      state.progressErrorCode = null;
      state.detectedType = null;
      state.detectedConfidence = null;
      state.detectedLabel = null;
    },
    applyOutlineProgress: (state, { payload }) => {
      const { eventType, data = {} } = payload || {};
      const { progress, timestamp, message } = data;
      if (typeof progress === "number") {
        state.overallProgress = Math.max(state.overallProgress, progress);
      }
      const phase = PHASE_BY_EVENT[eventType];
      const phaseChanged = phase && phase !== state.currentPhase;
      if (phase) {
        state.currentPhase = phase;
        raiseProgressToPhaseFloor(state, phase);
      }
      if (eventType === "started" && timestamp) {
        state.generationStartedAt = timestamp;
      }
      if (message) {
        state.currentMessage = message;
      } else if (phaseChanged) {
        // A new phase with no message of its own falls back to the phase default
        // (see OutlineGenerationProgress); clear the prior phase's sticky message.
        state.currentMessage = null;
      }
    },
    applyDocumentProgress: (state, { payload = {} }) => {
      const {
        documentId,
        documentName,
        documentIndex,
        documentTotal,
        documentStatus,
        batchCurrent,
        batchTotal,
        requirementsFound,
        newRequirements,
        progress,
        message,
      } = payload;

      if (typeof progress === "number") {
        state.overallProgress = Math.max(state.overallProgress, progress);
      }
      const batchFraction =
        typeof batchCurrent === "number" && batchTotal > 0
          ? Math.min(1, batchCurrent / batchTotal)
          : 0;
      state.overallProgress = Math.max(
        state.overallProgress,
        EXTRACTION_PROGRESS_START +
          Math.round(
            (EXTRACTION_PROGRESS_END - EXTRACTION_PROGRESS_START) *
              batchFraction
          )
      );
      if (typeof documentTotal === "number") {
        state.documentTotal = documentTotal;
      }
      if (typeof requirementsFound === "number") {
        state.requirementsFound = requirementsFound;
      }
      const phaseChanged = state.currentPhase !== "extracting";
      state.currentPhase = "extracting";
      if (message) {
        state.currentMessage = message;
      } else if (phaseChanged) {
        // Entering extraction with no message → show the phase default, not the prior
        // phase's sticky message.
        state.currentMessage = null;
      }

      if (typeof documentIndex === "number") {
        const idx = state.documentPipeline.findIndex(
          (d) => d.documentIndex === documentIndex
        );
        const entry = {
          documentId,
          documentName,
          documentIndex,
          documentTotal,
          status: documentStatus,
          batchCurrent,
          batchTotal,
        };
        if (idx >= 0) {
          state.documentPipeline[idx] = {
            ...state.documentPipeline[idx],
            ...entry,
          };
        } else {
          state.documentPipeline.push(entry);
        }
      }

      if (Array.isArray(newRequirements) && newRequirements.length) {
        const additions = newRequirements.map((entry) =>
          typeof entry === "string"
            ? { text: entry }
            : { text: entry.text, category: entry.category }
        );
        state.requirementsFeed = state.requirementsFeed.concat(additions);
      }
    },
    // Narrow companion to applyOutlineProgress: sets currentPhase (and clears the sticky
    // currentMessage on a phase change so the new phase's default shows) without touching
    // overallProgress/history. Used for gate-ready events (type_review_ready/req_review_ready),
    // whose SSE payload carries a stringified gate block in `message` rather than a
    // human-readable progress message — running it through applyOutlineProgress would stomp
    // currentMessage with that raw JSON.
    setCurrentPhase: (state, { payload }) => {
      raiseProgressToPhaseFloor(state, payload);
      if (payload && payload !== state.currentPhase) {
        state.currentPhase = payload;
        // Phase changed with no accompanying message → fall back to the phase default.
        state.currentMessage = null;
      }
    },
    applyClassificationDetected: (state, { payload = {} }) => {
      const { type, confidence, label } = payload;
      if (type) {
        state.detectedType = type;
      }
      if (typeof confidence === "number") {
        state.detectedConfidence = confidence;
      }
      if (label) {
        state.detectedLabel = label;
      }
    },
    setProgressError: (state, { payload }) => {
      state.progressError = payload || null;
    },
    setConnectionState: (state, { payload }) => {
      state.connectionState = payload || "idle";
    },
    setClarification: (state, { payload }) => {
      state.clarification = payload;
    },
    clearClarification: (state) => {
      state.clarification = null;
      state.clarificationAnswers = [];
    },
    // Task 6.4: explicit action for wiping accumulated answers. Dispatched
    // only from a fresh generate/regenerate — NOT from connect/resume/reconnect —
    // so retry-after-FAILED and watchdog reconnects keep prior answers intact.
    clearClarificationAnswers: (state) => {
      state.clarificationAnswers = [];
    },
    appendClarificationAnswers: (state, { payload = [] }) => {
      // Last-wins per questionId: resubmitting a prefilled answer (e.g. after a failed
      // resume) replaces the prior answer for that question instead of duplicating it.
      state.clarificationAnswers = mergeAnswersLastWins(
        state.clarificationAnswers,
        payload
      );
    },
    setDocumentIds: (state, { payload }) => {
      state.documentIds = payload;
    },
    setSelectedRowIds: (state, { payload = [] }) => {
      state.selectedRowIds = uniq(payload);
    },
    setSelectedIndex: (state, { payload = -1 }) => {
      state.selectedIndex = payload;
    },
    insertNewSectionAt: (state, { payload }) => {
      const { outlineData } = state.data || {};
      const { index, newSection } = payload;

      outlineData.sections = [
        ...outlineData.sections.slice(0, index + 1),
        newSection,
        ...outlineData.sections.slice(index + 1),
      ];
      state.selectedIndex = index + 1;
    },
    updateOutlineSections: (state, { payload = [] }) => {
      const { outlineData } = state.data || {};
      state.data = {
        ...state.data,
        outlineData: {
          ...outlineData,
          sections: payload,
        },
      };
      state.documentLookup = buildDocumentLookup(payload);
    },
    setGateState: (state, { payload }) => {
      state.gate = payload || null;
    },
    clearGateState: (state) => {
      state.gate = null;
    },
    setStructureProposal: (state, { payload }) => {
      state.structureProposal = payload || null;
    },
    clearStructureProposal: (state) => {
      state.structureProposal = null;
    },
    setRequirements: (state, { payload = [] }) => {
      state.requirements = payload;
    },
    upsertRequirement: (state, { payload }) => {
      if (!payload) {
        return;
      }
      const idx = state.requirements.findIndex((r) => r.id === payload.id);
      if (idx >= 0) {
        state.requirements[idx] = payload;
      } else {
        state.requirements.push(payload);
      }
    },
    removeRequirementLocal: (state, { payload: id }) => {
      const idx = state.requirements.findIndex((r) => r.id === id);
      if (idx >= 0) {
        state.requirements[idx] = {
          ...state.requirements[idx],
          status: REQUIREMENT_STATUS.REMOVED,
        };
      }
    },
    setOutlineDocuments: (state, { payload = [] }) => {
      state.outlineDocuments = payload;
    },
    setDocumentParagraphs: (state, { payload }) => {
      const { documentId, paragraphs = [] } = payload || {};
      if (documentId == null) {
        return;
      }
      state.documentParagraphs[documentId] = paragraphs;
    },
  },
});

export const {
  reset,
  resetEditing,
  setOwner,
  clearOwner,
  initializeEditing,
  setGenerateFailed,
  setIsEditing,
  setIsLocked,
  setOutlineData,
  setProcessingStatus,
  setProgressHistory,
  addProgressHistory,
  setProgressError,
  setConnectionState,
  setClarification,
  clearClarification,
  clearClarificationAnswers,
  appendClarificationAnswers,
  setDocumentIds,
  setSelectedRowIds,
  setSelectedIndex,
  insertNewSectionAt,
  updateOutlineSections,
  resetGenerationProgress,
  applyOutlineProgress,
  setCurrentPhase,
  applyDocumentProgress,
  applyClassificationDetected,
  setGateState,
  clearGateState,
  setStructureProposal,
  clearStructureProposal,
  setRequirements,
  upsertRequirement,
  removeRequirementLocal,
  setOutlineDocuments,
  setDocumentParagraphs,
} = outlineSlice.actions;

export default outlineSlice;
